import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { isSubscriptionsEnabled } from '@/utils/featureFlags'
import { ROUTES } from '../routes'

/**
 * RootRedirect Component
 *
 * Smart redirect from "/" based on user authentication state and onboarding status.
 *
 * Redirect logic:
 * - Not authenticated → /login
 * - Authenticated with incomplete onboarding → appropriate onboarding step
 * - Authenticated and complete → /social-media (default dashboard)
 */
export const RootRedirect: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore()

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  // Authenticated but incomplete onboarding → redirect to onboarding step
  if (isAuthenticated && user) {
    const { onboardingStatus, roleName } = user

    // System admins bypass onboarding checks
    const isSystemAdmin = roleName === 'system_admin'
    const isOnboardingComplete =
      onboardingStatus === 'completed' || onboardingStatus === 'admin_created'

    // Check if onboarding is incomplete (only if subscriptions enabled)
    if (
      isSubscriptionsEnabled() &&
      !isSystemAdmin &&
      !isOnboardingComplete
    ) {
      // Redirect based on onboarding status
      switch (onboardingStatus) {
        case 'pending_verification':
          return <Navigate to={ROUTES.ONBOARDING.VERIFY_EMAIL} replace />

        case 'verified_unpaid':
          return <Navigate to={ROUTES.ONBOARDING.CHECKOUT} replace />

        case 'payment_pending':
          return <Navigate to={ROUTES.ONBOARDING.SUCCESS} replace />

        default:
          // Unknown status, treat as complete
          break
      }
    }
  }

  // Authenticated and complete → redirect to default dashboard
  return <Navigate to={ROUTES.SOCIAL_MEDIA} replace />
}
